import { useState, useEffect, useCallback } from "react";

import axios from "../utils/axios";

import { Card, Table, Button } from "antd";
import Box from "../components/Box";
import SearchInput from "../components/SearchInput";

import { blockUrl } from "../utils/urls";

function BlockTable() {
  // State
  const [lastBlockHeight, setLastBlockHeight] = useState(null);
  const [blocks, setBlocks] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchedBlock, setSearchedBlock] = useState(null);
  const [isSearched, setIsSearched] = useState(false);

  const onHeightColumnClick = (blockHeight) => {
    window.open(`/blocks/${blockHeight}`, "_blank");
  };

  const columns = [
    {
      title: "Height",
      render: (record) => {
        return (
          <Box>
            <Button
              data-height={record.header.height}
              onClick={() => onHeightColumnClick(record.header.height)}
              type="link"
            >
              {record.header.height}
            </Button>
          </Box>
        );
      },
    },
    {
      title: "Hash",
      render: (record) => {
        const hash = record.block_id;
        const shortHash = `${hash.slice(0, 6)}...${hash.slice(-6)}`;
        return <Box>{shortHash}</Box>;
      },
    },
    {
      title: "Proposer",
      render: (record) => {
        return <Box>{record.header.proposer_address}</Box>;
      },
    },
    {
      title: "Tx Count",
      render: (record) => {
        const txHashes = record.tx_hashes;
        const txCount = txHashes.length;
        return <Box>{txCount}</Box>;
      },
    },
    {
      title: "Time",
      render: (record) => {
        return <Box>{record.header.time}</Box>;
      },
    },
  ];

  const fetchLastBlock = useCallback(() => {
    const url = `${blockUrl}/last`;

    if (isSearched) {
      return;
    }

    axios.get(url).then((response) => {
      if (response.data) {
        const lastBlock = response.data;
        const lastBlockHeight = lastBlock.last_commit.height;
        setLastBlockHeight(lastBlockHeight);
      }
    });
  }, [isSearched]);

  const fetchBlock = useCallback(
    async (blockHeight) => {
      const url = `${blockUrl}/height/${blockHeight}`;

      if (isSearched) {
        return;
      }

      const blockNumbers = blocks.map((block) => block.header.height);
      if (blockNumbers.includes(blockHeight)) {
        return;
      }

      axios.get(url).then((response) => {
        if (response.data) {
          const blockHashes = blocks.map((block) => block.block_id);

          if (!blockHashes.includes(response.data.block_id)) {
            setBlocks((prevBlocks) => {
              return [...prevBlocks, response.data].filter(
                (value, index, self) => {
                  return (
                    self.findIndex((v) => v.block_id === value.block_id) ===
                    index
                  );
                },
              );
            });
          }
        }
      });
    },
    [blocks, isSearched],
  );

  const onSearch = (blockHeight) => {
    if (!blockHeight) {
      return;
    }

    setIsSearching(true);
    setIsSearched(true);
    setSearchedBlock(null);

    const url = `${blockUrl}/height/${blockHeight}`;
    axios
      .get(url)
      .then((response) => {
        if (response.data) {
          console.log(response.data);
          setSearchedBlock(response.data);
        }
      })
      .catch(() => {})
      .finally(() => {
        setIsSearching(false);
      });
  };

  const getDatasources = useCallback(() => {
    if (isSearched) {
      if (!searchedBlock) {
        return [];
      }

      return [searchedBlock];
    }

    return blocks
      .sort((a, b) => b.header.height - a.header.height)
      .slice(0, 10);
  }, [isSearched, blocks, searchedBlock]);

  useEffect(() => {
    fetchLastBlock();
  }, [fetchLastBlock]);

  useEffect(() => {
    const interval = setInterval(fetchLastBlock, 5000);
    return () => clearInterval(interval);
  }, [fetchLastBlock]);

  useEffect(() => {
    if (lastBlockHeight) {
      for (let i = lastBlockHeight; i > lastBlockHeight - 10; i--) {
        fetchBlock(i);
      }
    }
  }, [lastBlockHeight, fetchBlock]);

  return (
    <Box mt={16}>
      <Card>
        <h1>Recent Blocks</h1>
        <SearchInput
          placeholder="Enter block height"
          enterButton="Search"
          size="large"
          disabled={isSearching}
          isLoading={isSearching}
          onSearch={onSearch}
        />
        <Table
          style={{ marginTop: "16px" }}
          columns={columns}
          dataSource={getDatasources()}
          rowKey="hash"
          pagination={false}
        />
      </Card>
    </Box>
  );
}

export default BlockTable;
